import { AxiosRequestConfig } from 'axios';
import url from 'url';

import { BaseAPI, prepareRequestAndFillFormData } from '@shared/api/base-api';

export interface P5ReportBestCompliance {
  enabled: boolean;
  days: number;
  threshold_Hours: number;
  includeHft: boolean;
}

export interface ComplianceGoalSettings {
  /** Goal calculation enabled/disabled */
  enabled: boolean;
  /** Compliance threshold  */
  usageThreshold_Hours: number;
  /**
   * Number of compliant days in the defined period calculationPeriodDays which should be achieved to meet the goal.
   */
  numberOfDays: number;
  /**
   * Number of days which should be used to search for compliant days.
   */
  calculationPeriodDays: number;
}

/**
 * Dto describing all available settings for an organisation
 * @export
 * @interface Settings
 */
export interface EditableSettingsDto {
  isTwoFactorAuthenticationEnabled: boolean;
  isDeviceSettingsConfirmationRequiresPassword: boolean;
  isPicGenerationForbidden: boolean;
  p5BestCompliance: P5ReportBestCompliance;
  complianceGoal: ComplianceGoalSettings;
  isTelemonitoringEnabled: boolean;
  logoExists: boolean;
  isPatientAppEnabled: boolean;
  allowBluetoothData: boolean;
  complianceReportIncludeConfiguration: boolean;
}

export const SettingsApi = (baseApi: BaseAPI) => {
  const basePath = '/organisations/settings';

  return {
    /**
     * Returns settings which can be edited via WebApp.
     * @returns DTO with settings.
     */
    async getSettings(options?: AxiosRequestConfig): Promise<EditableSettingsDto> {
      const { data } = await baseApi.get<EditableSettingsDto>(basePath, undefined, options);
      return data;
    },

    /**
     * Enables the requirement to confirm device configuration changes.
     */
    async activateConfirmSettingsRequired() {
      await baseApi.post(`${basePath}/configurationConfirmationRequirement`);
    },

    /**
     * Disables the requirement to confirm device configuration changes.
     */
    async deactivateConfirmSettingsRequired() {
      await baseApi.delete(`${basePath}/configurationConfirmationRequirement`);
    },

    /**
     * Enables two factor authentication.
     */
    async activateTwoFactorAuthentication() {
      await baseApi.post(`${basePath}/2fa/activation`);
    },

    /**
     * Disables two factor authentication
     */
    async deactivateTwoFactorAuthentication() {
      await baseApi.delete(`${basePath}/2fa/activation`);
    },

    async enablePatientAppAccess() {
      await baseApi.post(`${basePath}/patientApp/access`);
    },

    async disablePatientAppAccess() {
      await baseApi.delete(`${basePath}/patientApp/access`);
    },

    async activateBluetoothDataUpload() {
      await baseApi.post(`${basePath}/patientApp/bluetoothUpload`);
    },

    async deactivateBluetoothDataUpload() {
      await baseApi.delete(`${basePath}/patientApp/bluetoothUpload`);
    },

    async allowReportComplianceGoal() {
      await baseApi.post(`${basePath}/complianceGoal`);
    },

    async disableReportComplianceGoal() {
      await baseApi.delete(`${basePath}/complianceGoal`);
    },

    async updateComplianceGoalNumberOfDays(numberOfDays: number, calculationPeriodDays: number) {
      const params = url.format({ query: { numberOfDays, calculationPeriodDays } });
      await baseApi.post(`${basePath}/complianceGoal/days${params}`);
    },

    async EnableComplianceReportIncludeConfiguration() {
      await baseApi.post(`${basePath}/complianceReportIncludeConfiguration`);
    },

    async DisableComplianceReportIncludeConfiguration() {
      await baseApi.delete(`${basePath}/complianceReportIncludeConfiguration`);
    },

    async updateComplianceGoalCalculationPeriodDays(days: number) {
      const params = url.format({ query: { days } });
      await baseApi.post(`${basePath}/complianceGoal/periodDays${params}`);
    },

    /**
     * Enables best compliance report.
     */
    async allowReportBestCompliance() {
      await baseApi.post(`${basePath}/p5BestCompliance`);
    },

    /**
     * Disables best compliance report.
     */
    async disableReportBestCompliance() {
      await baseApi.delete(`${basePath}/p5BestCompliance`);
    },

    /**
     * Updates number of compliance days.
     * @param days
     */
    async updateNumberOfBestComplianceDays(days: number) {
      const params = url.format({ query: { days } });
      await baseApi.post(`${basePath}/p5BestCompliance/days${params}`);
    },

    /**
     * Updates best compliance usage threshold.
     * @param threshold
     */
    async updateBestComplianceUsageThreshold(threshold: number) {
      const params = url.format({ query: { threshold } });
      await baseApi.post(`${basePath}/p5BestCompliance/threshold${params}`);
    },

    /**
     * Enable telemonitoring for all newly registered P2.8W devices.
     */
    async activateTelemonitoringForOrganisation() {
      await baseApi.post(`${basePath}/telemonitoringEnabled`);
    },

    /**
     * Disables telemonitoring for all newly registered P2.8W devices.
     */
    async deactivateTelemonitoringForOrganisation() {
      await baseApi.delete(`${basePath}/telemonitoringEnabled`);
    },

    /**
     * Uploads an organisation logo.
     * @param logo Logo file to be uploaded.
     */
    async uploadLogo(logo: File): Promise<void> {
      const localVarAxiosArgs = prepareRequestAndFillFormData(logo, undefined, `${basePath}/logo`);
      const axiosRequestArgs = {
        ...localVarAxiosArgs.options,
        url: localVarAxiosArgs.url,
      };
      await baseApi.request(axiosRequestArgs);
    },

    /**
     * Removes an organisation logo.
     */
    async removeLogo(): Promise<void> {
      await baseApi.delete(`${basePath}/logo`);
    },

    async includeHftIntoBestCompliance() {
      await baseApi.post(`${basePath}/p5BestCompliance/includeHft?includeHft=true`);
    },

    async excludeHftFromBestCompliance() {
      await baseApi.post(`${basePath}/p5BestCompliance/includeHft?includeHft=false`);
    },
  };
};

export const SettingsApiFactory = (baseApi: BaseAPI) => SettingsApi(baseApi);
